import { useContext, useState } from "react";
import { generatePrincipalGeaoDocuments, generateProducerGeaoDocuments } from "../../../../../api/docusign";
import { GeaoContext } from "../../../GeaoContextProvider";
import { update, updateMany } from "../../../../../api/onboarding";
import { classes } from "../../../../../util/style-utils";
import { FlowType } from "../../../constants/FlowType";
import Loader, { Types } from "../../../../gdk/Loader";
import { isCobrandedSubAgency } from "../../../mappers/InterviewMapper";

export default function ReviewAndSignDocumentsLink({ flowType, documentsWereSigned = false }) {
    const {
        agentInterview,
        setAgentInterview,
        agencyInterview,
        setAgencyInterview
    } = useContext(GeaoContext);

    const [showLoader, setShowLoader] = useState(false);

    const hasSignedFcra = agentInterview?.questionAnswers?.HasSignedFCRA?.answer === "true";
    const isCobranded = isCobrandedSubAgency(agencyInterview);
    const interviewIsIncomplete = agentInterview?.outcome?.toLowerCase() === "incomplete";
    // Use window.location.search directly instead of useSearchParams hook to avoid timing issues on DocuSign redirect
    const justReturnedFromDocuSign = new URLSearchParams(window.location.search).get("event") === "signing_complete";

    // For cobranded agencies: Allow re-signing if signed but interview is still incomplete (not submitted)
    // and not just returned from DocuSign. This handles the case where the user signed forms but exited
    // without submitting, then recalled their interview.
    // For non-cobranded agencies: Only allow signing if not yet signed.
    // Also prevent re-triggering if documents were just signed in this session.
    const canSign = !documentsWereSigned && (!hasSignedFcra || (isCobranded && interviewIsIncomplete && !justReturnedFromDocuSign));

    const onReviewAndSignClick = () => {
        setShowLoader(true);

        if (flowType === FlowType.Principal) {
            generateDocumentsForPrincipalFlow();
        }

        if (flowType === FlowType.Producer) {
            generateDocumentsForProducerFlow();
        }
    };

    const generateDocumentsForPrincipalFlow = () => {
        // Current URL without query parameters.
        const postDocuSignUrl = window.location.origin + window.location.pathname;

        generatePrincipalGeaoDocuments({
            PostDocuSignUrl: postDocuSignUrl
        }, handlePrincipalDocuSignResponse);
    }

    const handlePrincipalDocuSignResponse = (eoResponse) => {
        const updateAgentInterviewRequest = {
            identityType: "Agent",
            interview: {
                ...agentInterview,
                questionAnswers: {
                    ...agentInterview?.questionAnswers,
                    DocuSignEnvelopeId: { answer: eoResponse.envelopeId },
                    DocuSignOrchMessageId: { answer: eoResponse.orchMessageId }
                }
            }
        };

        const updateAgencyInterviewRequest = {
            identityType: "Agency",
            interview: {
                ...agencyInterview,
                questionAnswers: {
                    ...agencyInterview?.questionAnswers,
                    DocuSignEnvelopeId: { answer: eoResponse.envelopeId },
                    DocuSignOrchMessageId: { answer: eoResponse.orchMessageId }
                }
            }
        };

        updateMany([
            updateAgentInterviewRequest,
            updateAgencyInterviewRequest
        ],
            interviews => {
                setAgentInterview(interviews.find(i => i.interviewId === agentInterview.interviewId));
                setAgencyInterview(interviews.find(i => i.interviewId === agencyInterview.interviewId));
                window.location.href = eoResponse.docServiceUrl;
            }
        );
    };

    const generateDocumentsForProducerFlow = () => {
        // Current URL without query parameters.
        const postDocuSignUrl = window.location.origin + window.location.pathname;

        generateProducerGeaoDocuments({
            PostDocuSignUrl: postDocuSignUrl
        }, handleProducerDocuSignResponse);
    };

    const handleProducerDocuSignResponse = (eoResponse) => {
        const updateAgentRequest = {
            identityType: "Agent",
            interview: {
                ...agentInterview,
                questionAnswers: {
                    ...agentInterview?.questionAnswers,
                    DocuSignEnvelopeId: { answer: eoResponse.envelopeId },
                    DocuSignOrchMessageId: { answer: eoResponse.orchMessageId }
                }
            }
        };

        update(updateAgentRequest, responseInterview => {
            setAgentInterview(responseInterview);
            window.location.href = eoResponse.docServiceUrl;
        });
    };

    if (showLoader) {
        return <Loader type={Types.Section} />
    }

    return (
        <ul className={classes("list list--unordered", !canSign && "list--checkmark")}>
            <li>
                {canSign ? <button data-onboarding-button-id="shared:ReviewAndSign:ReviewAndSignDocumentsLink:ReviewAndSignForms" className="link-button" onClick={onReviewAndSignClick}>Review and Sign Forms</button> : "Review and Sign Forms"}
            </li>
        </ul>
    );
}